import type { Direction, Point } from "@/core/types";
import { oppositeDirection } from "@/levels/geometry";
import { isAnyHazardLethal, type AnyHazardContext } from "./hazards";

/**
 * Stateless geometry helpers shared by every maneuver detector below.
 * Deliberately framework-independent (no Phaser, no `LevelGame` instance)
 * so they can be unit tested directly against synthetic board geometry -
 * `ManeuverTracker` is the only thing that calls these as part of a real run.
 */

export type MovementAxis = "h" | "v";

export function axisOf(direction: Direction): MovementAxis {
  return direction === "up" || direction === "down" ? "v" : "h";
}

/** Two directions are perpendicular exactly when they're on different axes - covers every rotation/reflection without enumerating cases. */
export function isPerpendicular(a: Direction, b: Direction): boolean {
  return axisOf(a) !== axisOf(b);
}

// ---------------------------------------------------------------------
// PERFECT FOLD
// ---------------------------------------------------------------------

/**
 * `directionHistory` is the last (up to 3) actually-executed directions,
 * oldest first. A fold is A -> perpendicular B -> opposite(A), checked
 * against exactly the last 3 entries - since each entry is exactly one
 * executed movement step, this alone enforces the "minimum-width" rule: a
 * wider turn (B lasting 2+ steps) can never line up as the last 3 entries
 * matching this exact pattern.
 */
export function detectPerfectFold(directionHistory: readonly Direction[], currentLength: number, minLength: number): boolean {
  if (currentLength < minLength) return false;
  if (directionHistory.length < 3) return false;
  const [a, b, c] = directionHistory.slice(-3);
  return isPerpendicular(a, b) && c === oppositeDirection(a);
}

// ---------------------------------------------------------------------
// LAST-SECOND TURN
// ---------------------------------------------------------------------

export interface LastSecondTurnInput {
  prevDirection: Direction;
  direction: Direction;
  /**
   * PR26 CHANGE 1 (LAST-SECOND TURN V2): whether continuing straight in
   * `prevDirection` would have been lethal under production's own
   * authoritative collision rules - wall/boundary (when no legal wrap
   * exists), fixed obstacle, the snake's own body, or BLACK HOLE. Always
   * pre-computed by the caller (`LevelGame.wouldContinuingStraightBeLethal()`)
   * against the PRE-move state, using the exact same resolution order
   * `tick()` itself uses (including portal/WORMHOLE resolution) - this
   * function never re-derives hazard geometry itself, so it can never
   * disagree with what production would actually have done.
   */
  continuingStraightWouldBeLethal: boolean;
}

/**
 * True when the player turned (a valid perpendicular move) instead of
 * continuing straight into an otherwise-fatal next movement. Non-qualifying
 * by construction whenever `continuingStraightWouldBeLethal` is false -
 * continuing straight is actually safe, a valid fixed/random portal
 * traversal, or a valid active-WORMHOLE boundary wrap. Destination safety
 * (of the turn actually taken) is the caller's responsibility - this is
 * only ever invoked for a move that already resolved safely.
 */
export function detectLastSecondTurn(input: LastSecondTurnInput): boolean {
  if (!isPerpendicular(input.prevDirection, input.direction)) return false;
  return input.continuingStraightWouldBeLethal;
}

// ---------------------------------------------------------------------
// TAIL GRAZE
// ---------------------------------------------------------------------

/** Body indices closer to the head than this (the neck and near-head body) never count as "distant" for TAIL GRAZE. */
export const TAIL_GRAZE_MIN_INDEX = 4;

/** `bodyAfterMove` must already reflect this move's tail-vacating semantics (post-movement body), head first. */
export function distantTailSegments(bodyAfterMove: readonly Point[]): readonly Point[] {
  return bodyAfterMove.slice(TAIL_GRAZE_MIN_INDEX);
}

function manhattanDistance(a: Point, b: Point): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

/** True when the new head sits at Manhattan distance exactly 1 from any distant (index >= 4) live body segment. Multiple qualifying segments still resolve to a single `true`. */
export function isAdjacentToDistantBody(headAfter: Point, bodyAfterMove: readonly Point[]): boolean {
  return distantTailSegments(bodyAfterMove).some((segment) => manhattanDistance(headAfter, segment) === 1);
}

// ---------------------------------------------------------------------
// THREAD THE NEEDLE
// ---------------------------------------------------------------------

export interface SqueezedCorridorInput extends AnyHazardContext {
  headAfter: Point;
  direction: Direction;
}

/** True when both cells perpendicular to the current movement axis, at `headAfter`, are currently lethal hazards (wall/obstacle/body, in any mix). */
export function isSqueezedCorridorCell(input: SqueezedCorridorInput): boolean {
  const axis = axisOf(input.direction);
  const perpendicularCells: Point[] =
    axis === "h"
      ? [
          { x: input.headAfter.x, y: input.headAfter.y - 1 },
          { x: input.headAfter.x, y: input.headAfter.y + 1 },
        ]
      : [
          { x: input.headAfter.x - 1, y: input.headAfter.y },
          { x: input.headAfter.x + 1, y: input.headAfter.y },
        ];
  return perpendicularCells.every((cell) => isAnyHazardLethal(cell, input));
}
